<template lang="pug">
.popover-container(:class="popoverContainerClasses")
  span(
    ref="triggerRef"
    :class="`d-flex ${spanClass || ''}`"
    v-on="triggerListeners"
  )
    slot(name="trigger")
  teleport(to="body")
    //- Wrapper keeps `.vue-popover` from being a direct child of <body>. Legacy responsive-tables.js
    //- hides Vue popovers on horizontal table scroll via `$('.vue-popover').parent().hide()` — with a
    //- bare teleport its parent is <body>, so that call would hide the whole page. A <span> for
    //- Vue 2 parity: VuePopper's append-to-body wrapper was a span, and selectors match
    //- `body > span > .popover`.
    span.vue-popover-wrapper(v-if="loadPopover")
      .popover.vue-popover.fade.in(
        v-show="showPopper"
        :id="id"
        ref="popoverRef"
        :class="classes"
        @mouseenter="onPopoverMouseEnter"
        @mouseleave="onPopoverMouseLeave"
      )
        h3.popover-title.ellipsis.d-flex.align-items-center.justify-content-between(v-if="showHeader")
          slot(name="header")
          button.close.header-close(
            v-if="showCloseButton"
            type="button"
            aria-hidden="true"
            @click="close"
          )
            i.hi.hi-close.hi-9
        .popover-content
          button.close(
            v-if="showCloseButton && !showHeader"
            type="button"
            aria-hidden="true"
            @click="close"
          )
            i.hi.hi-close.hi-9
          slot(name="body")
          slot
</template>

<script>
import Popper from 'popper.js';

export default {
  name: 'AppPopover',
  props: {
    popoverContainerClasses: {
      type: String,
      default: 'is-block',
    },
    placement: {
      type: String,
      default: 'top-start',
    },
    extraPopoverClasses: {
      type: String,
      default: '',
    },
    rootClass: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    dropdown: {
      type: Boolean,
      default: false,
    },
    boundariesElement: {
      type: String,
      default: 'viewport',
    },
    id: {
      type: String,
      default: '',
    },
    hideClose: {
      type: Boolean,
      default: false,
    },
    trigger: {
      type: String,
      default: 'click',
    },
    delay: {
      type: Number,
      default: 400,
    },
    lazy: {
      type: Boolean,
      default: false,
    },
    showHeader: {
      type: Boolean,
      default: false,
    },
    reference: {
      type: Element,
    },
    hideFromAction: {
      type: Boolean,
      default: false,
    },
    spanClass: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      loadPopover: !this.lazy,
      showPopper: false,
      popperInstance: null,
      hoverDelayTimer: null,
      outsideClickRegistration: null,
    };
  },
  computed: {
    classes() {
      let classes = this.extraPopoverClasses;

      if (this.dropdown) {
        classes += ' popover-dropdown';
      }

      return classes;
    },
    showCloseButton() {
      return !(this.isHover || this.dropdown || this.hideClose);
    },
    isHover() {
      return this.trigger === 'hover';
    },
    triggerListeners() {
      if (this.disabled) {return {};}
      if (this.isHover) {
        return {
          mouseenter: this.onTriggerMouseEnter,
          mouseleave: this.onTriggerMouseLeave,
          focus: this.open,
        };
      }

      return { click: this.toggle };
    },
  },
  watch: {
    hideFromAction(newVal) {
      if (newVal) {
        this.close();
      }
    },
    showPopper(val) {
      if (val) {
        this.$emit('show');
        this.$nextTick(() => this.updatePopper());
      } else {
        this.$emit('hide', true);
      }
    },
  },
  mounted() {
    if (this.loadPopover) {
      this.$nextTick(() => this.initPopper());
    }
    // Register on the next macrotask: when the popover is mounted in response to a click
    // (e.g. the time off balances edit popover), that click is still bubbling and would
    // reach this brand-new document listener, closing the popover as soon as it opens.
    this.outsideClickRegistration = setTimeout(() => {
      document.addEventListener('click', this.onOutsideClick);
    });
  },
  beforeUnmount() {
    this.destroyPopper();
    clearTimeout(this.outsideClickRegistration);
    document.removeEventListener('click', this.onOutsideClick);
    clearTimeout(this.hoverDelayTimer);
  },
  methods: {
    getReferenceEl() {
      return this.reference || this.$refs.triggerRef;
    },
    initPopper() {
      if (!this.$refs.popoverRef) {return;}
      const reference = this.getReferenceEl();

      if (!reference) {return;}

      this.popperInstance = new Popper(reference, this.$refs.popoverRef, {
        placement: this.placement,
        modifiers: {
          hide: { enabled: true },
          preventOverflow: { boundariesElement: this.boundariesElement },
        },
      });
      this.$emit('created', true);
    },
    destroyPopper() {
      if (this.popperInstance) {
        this.popperInstance.destroy();
        this.popperInstance = null;
      }
    },
    updatePopper() {
      if (!this.popperInstance) {
        this.initPopper();
      } else {
        this.popperInstance.scheduleUpdate();
      }
    },
    open() {
      if (this.disabled) {return;}
      if (!this.loadPopover) {
        this.loadPopover = true;
        this.$nextTick(() => {
          this.initPopper();
          this.showPopper = true;
        });

        return;
      }
      this.showPopper = true;
    },
    close() {
      this.showPopper = false;
    },
    toggle() {
      if (this.showPopper) {
        this.close();
      } else {
        this.open();
      }
    },
    onTriggerMouseEnter() {
      clearTimeout(this.hoverDelayTimer);
      this.hoverDelayTimer = setTimeout(() => this.open(), this.delay);
    },
    onTriggerMouseLeave() {
      clearTimeout(this.hoverDelayTimer);
      this.hoverDelayTimer = setTimeout(() => this.close(), this.delay);
    },
    onPopoverMouseEnter() {
      if (this.isHover) {
        clearTimeout(this.hoverDelayTimer);
      }
    },
    onPopoverMouseLeave() {
      if (this.isHover) {
        this.hoverDelayTimer = setTimeout(() => this.close(), this.delay);
      }
    },
    onOutsideClick(event) {
      if (!this.showPopper || this.isHover) {return;}
      const trigger = this.$refs.triggerRef;
      const popover = this.$refs.popoverRef;

      if (
        trigger && !trigger.contains(event.target) &&
        popover && !popover.contains(event.target)
      ) {
        this.close();
      }
    },
  },
};
</script>
